import React, { useState, useEffect, useRef, useCallback } from 'react';
import { LayoutCol } from '@/components/Elements/Layout';
import 'rsuite/dist/rsuite.min.css';
import Button from '@/components/Button';
import { Button as TsButton } from '@/components/TSX components';
import Input from '@/components/Input';
import LatestToggle from '@/components/Toggle';
import { useAuth } from '@/lib/auth';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  CaretDown,
  CaretRight,
  CaretUp,
  CheckCircle,
  Circle,
  Info,
  MagnifyingGlass,
  Plus,
  XCircle,
} from '@phosphor-icons/react';
import { useGetTableColumnsList } from '../api/dataCatalog/getTableColumns';
import { FilterDropDownPillDataCatalog } from '../components/FilterDropdownPillDataCatalog';
import { useCreateKeyDefinition } from '../api/dataCatalog/createKeyDefinition';
import { useDetectScrolledToBottom } from '@/hooks/useDetectScrolledToBottom';
import debounce from 'lodash/debounce';
import { twJoin, twMerge } from 'tailwind-merge';
import { ClickAwayListener, Fade, Popper } from '@mui/material';
import map from 'lodash/map';
import LatestToolTip from '@/components/Tooltip';
import { useGetConnectedDefinitionsList } from '../api/dataCatalog/getConnectedDefinitionsList';
import { useUpdateKeyDefinition } from '../api/dataCatalog/updateKeyDefinition';
import KDDropdown from '../components/KDDropdown';
import { SourcesModal } from '../components/SourcesModal';
import { useGetDictionariesList } from '../api/dataCatalog/getDictionariesList';
import { isEqual, uniq } from 'lodash';
import { SourcesElement } from '../components/SourcesElement';
import { EditColumnDetailModal } from '../components/EditColumnDetailModal';
import { useBlockNavigation } from '../../dashboard/hooks/useBlockNavigation';
import { RouterKeyDefPauseModal } from '../components/RouterKeyDefPauseModal';
import { ColumnSampleDataModal } from '../components/ColumnSampleDataModal';
import { useGetLogoList } from '../api/dataCatalog/getLogoList';
import { SelectDrillDowns } from '../../explore/searchMetric/SelectDrillDowns';
import useExploreStore from '../../explore/store/useExploreStore';
import { useShallow } from 'zustand/react/shallow';
import { updateDrillDownTables } from '../../explore/searchMetric/api/updateDrillDownTables';
import { useUpdateDefinition } from '../api/updateDefinition';
import { useUpdateDefinitionMergePaths } from '../../explore/searchMetric/api/updateDefinitionMergePaths';
import Skeleton from '@/components/Skeleton';
import spinner from '@/features/explore/assets/spinner.gif';
import { ChangeDefaultPathModal } from '../../explore/components/ChangeDefaultPathModal';
import Markdown from 'react-markdown';

export const DropDown = ({
  dat,
  searchQuery,
  active,
  setSources,
  sources,
  setSourcesTableMapping,
  hideIcon = false,
  bkpSources,
  bkpSourcesTableMapping,
  customPositionClass = '',
  isDictionaryPage = false,
  activeTable,
  setActiveTable,
}) => {
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [node, setRef] = useState();
  const [listData, setListData] = useState([]);
  const columnListData = useGetTableColumnsList({
    tableId: dat.id,
    keyword: searchQuery,
    pageNumber: currentPageNumber,
    config: {
      staleTime: Infinity,
      enabled:
        activeTable === dat.id &&
        (currentPageNumber === 1 || currentPageNumber <= totalPages) &&
        !dat.disabledForGuidedAnalysis,
    },
  });
  const { isBottom } = useDetectScrolledToBottom(node);

  useEffect(() => {
    columnListData?.data && setTotalPages(columnListData?.data?.totalPages);
    columnListData?.data &&
      setListData((prev) => {
        let clonedArr = structuredClone(prev);
        return [...clonedArr, ...columnListData.data.data];
      });
  }, [columnListData?.data]);
  useEffect(() => {
    if (isBottom === true && currentPageNumber < totalPages) {
      setCurrentPageNumber(currentPageNumber + 1);
    }
  }, [isBottom]);

  return (
    <FilterDropDownPillDataCatalog
      title={
        <div
          className={`gap-2 flex items-center text-xs font-normal leading-5 w-full text-left justify-between pr-4 ${
            dat.disabledForGuidedAnalysis
              ? `text-t-disabled`
              : `text-brand-ai-200 hover:bg-pv-primary-primary-50`
          } ${
            active &&
            !dat.disabledForGuidedAnalysis &&
            'bg-pv-primary-primary-50'
          } ${!hideIcon && 'pl-4'}`}
        >
          <div className="flex gap-2">
            <LatestToolTip placement="top" title={dat?.datasource_description}>
              <span>
                {hideIcon ? null : (
                  <img src={dat.integration_logo} alt="" width={24} />
                )}
              </span>
            </LatestToolTip>
            <div
              className="flex items-center gap-2"
              style={{
                ...(!isDictionaryPage && {
                  width: '240px',
                  wordWrap: 'break-word',
                }),
              }}
            >
              <LatestToolTip
                tooltipActive={!dat.disabledForGuidedAnalysis}
                placement="top"
                title={dat.name}
                displayTooltipOnOverflow={true}
              >
                <span
                  style={{
                    display: 'inline-block',
                    maxWidth: isDictionaryPage ? '130px' : '240px',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {dat.name}
                </span>
              </LatestToolTip>

              {searchQuery ? ` (${dat.column_count})` : null}
            </div>
          </div>
          <div className="rounded-lg bg-pv-primary-primary-500 px-2 text-white">
            {sources[dat.id]?.length}
          </div>
        </div>
      }
      listData={listData}
      setSources={setSources}
      sources={sources}
      tableId={dat.id}
      indiData={dat}
      setSourcesTableMapping={setSourcesTableMapping}
      active={dat.id === activeTable && !dat.disabledForGuidedAnalysis}
      setRef={setRef}
      bkpSources={bkpSources}
      bkpSourcesTableMapping={bkpSourcesTableMapping}
      columnListData={columnListData}
      disabled={dat.disabledForGuidedAnalysis}
      setActiveTable={setActiveTable}
      hideIcon={hideIcon}
    />
  );
};

const Type = ({ user, form, setForm, disabled }) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget || event.current);
    setOpen(true);
  };
  const typeEnum = {
    term: 'bg-pv-tags-blue',
    metric: 'bg-pv-tags-pink',
  };
  const options = [
    { label: 'Term', value: 'term' },
    { label: 'Metric', value: 'metric' },
  ];
  return (
    <div className="">
      <button
        className="w-full"
        disabled={user?.role.toLowerCase() !== 'admin' || disabled}
        onClick={handleClick}
      >
        <div
          className={twMerge(
            `items-center flex border border-brand-ai-300 m-h-9 rounded-lg focus:ring-brand-ai-100 focus:border-brand-ai-100 hover:border-brand-ai-100 justify-between py-2 px-3`,
            open && 'border border-brand-ai-100',
            disabled &&
              'bg-pv-neutral-grey-50 border-transparent hover:border-transparent'
          )}
        >
          <div
            className="mr-3 items-center flex overflow-hidden flex-wrap gap-1.5"
            style={{ color: '#abadb1' }}
          >
            {form?.category ? (
              <div
                className={twMerge(
                  `text-xs text-pv-text-primary-text flex gap-2 items-center
                  w-fit cursor-pointer rounded-lg py-0.5 px-2 capitalize`,
                  typeEnum[form?.category],
                  disabled && 'text-pv-text-disabled-text cursor-not-allowed'
                )}
              >
                {form?.category}
              </div>
            ) : (
              <div className="items-center flex ml-1 text-pv-text-secondary-text">
                Select definitions type
              </div>
            )}
          </div>
          {open ? (
            <CaretUp size={20} color="#ADB2CE" />
          ) : (
            <CaretDown size={20} color="#ADB2CE" />
          )}
        </div>
      </button>
      <Popper
        open={open}
        anchorEl={anchorEl}
        transition
        disablePortal
        placement="bottom-start"
        sx={{ zIndex: 10, width: anchorEl && anchorEl?.clientWidth / 2 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <div className="relative">
              <ClickAwayListener
                onClickAway={() => {
                  setOpen(false);
                }}
              >
                <div
                  className={`flex flex-col
                       whitespace-nowrap bg-white rounded-md shadow-lg card-shadow overflow-hidden`}
                >
                  {options.map((option) => {
                    return (
                      <div
                        className={twMerge(
                          `hover:bg-pv-primary-primary-50 w-full cursor-pointer flex flex-start relative`,
                          form?.category === option.value && 'font-medium'
                        )}
                        onClick={() => {
                          setForm({ ...form, category: option.value });
                          setOpen(false);
                        }}
                      >
                        <label
                          className={`gap-3 px-3 py-2.5 flex items-center w-full cursor-pointer`}
                          style={{
                            accentColor: '#3661ED',
                          }}
                        >
                          <input
                            type="radio"
                            checked={form?.category === option.value}
                            className="flex"
                          />
                          <span
                            className={twMerge(
                              `text-xs text-pv-text-primary-text flex gap-2 items-center w-fit cursor-pointer rounded-lg py-0.5 px-2 capitalize`,
                              typeEnum[option.value]
                            )}
                          >
                            {option.value}
                          </span>
                        </label>
                        <LatestToolTip
                          className="absolute top-1/2 right-3 transform -translate-y-1/2"
                          placement="top"
                          title={
                            option.value === 'metric'
                              ? `Quantifiable measures used to track, assess, and
                          analyze business performance. Provides insights to
                          support data-driven decision-making. Examples: ARR,
                          Conversion Rate, Deal Size etc.`
                              : `Words or phrases describing business concepts and
                          ideas. Establish a common language and understanding
                          within the organization. Can include conditions.
                          Examples: Q1, Q2, Upsell, Churn, Customer etc.`
                          }
                        >
                          <Info
                            size={16}
                            className="text-pv-neutral-grey-600 hover:text-pv-neutral-grey-900"
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          />
                        </LatestToolTip>
                      </div>
                    );
                  })}
                </div>
              </ClickAwayListener>
            </div>
          </Fade>
        )}
      </Popper>
    </div>
  );
};

const ConnectedDefinitions = ({
  user,
  connectedDefinitions,
  setConnectedDefinitions,
  bkpConnectedDefinitions,
  setBkpConnectedDefinitions,
  connectedDefinitionsList,
}) => {
  const [anchorEl, setAnchorEl] = React.useState();
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const { keyDefinitionId } = useParams();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget || event.current);
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  const isChecked = (value) => {
    return connectedDefinitions?.find((item) => item._id === value._id);
  };
  const handleChange = (value) => {
    if (isChecked(value)) {
      setConnectedDefinitions((prev) => {
        return prev.filter((item) => item._id !== value._id);
      });
    } else {
      const item = connectedDefinitionsList.data.metrics.find(
        (e) => e._id === value._id
      );
      setConnectedDefinitions((prev) => [...prev, item]);
    }
  };

  let metricInfo = {};
  if (
    keyDefinitionId !== 'new-definition' &&
    !connectedDefinitionsList.isLoading &&
    Array.isArray(connectedDefinitionsList.data?.metrics)
  ) {
    metricInfo = connectedDefinitionsList.data.metrics?.filter(
      (dat) => dat._id === keyDefinitionId
    )[0];
  }

  return (
    <div className="">
      <button
        className="w-full"
        disabled={user?.role.toLowerCase() !== 'admin'}
        onClick={handleClick}
      >
        <div
          className={twMerge(
            `w-full items-center flex border border-brand-ai-300 m-h-9 rounded-lg focus:ring-brand-ai-100 focus:border-brand-ai-100 hover:border-brand-ai-100 justify-between py-2 px-3`,
            open && 'border border-brand-ai-100'
          )}
        >
          <div
            className="mr-3 items-center flex overflow-hidden flex-wrap gap-1.5"
            style={{ color: '#abadb1' }}
          >
            {connectedDefinitionsList?.data &&
            bkpConnectedDefinitions.length > 0 ? (
              <div className="items-center flex ml-1 text-pv-text-primary-text text-wrap text-left">
                {bkpConnectedDefinitions.map((cD) => cD?.name).join(' | ')}
              </div>
            ) : (
              <div className="items-center flex ml-1 text-pv-text-secondary-text">
                Select connected definitions
              </div>
            )}
          </div>
          <div>
            {open ? (
              <CaretUp size={20} color="#ADB2CE" />
            ) : (
              <CaretDown size={20} color="#ADB2CE" />
            )}
          </div>
        </div>
      </button>
      <div>
        <Popper
          open={open}
          anchorEl={anchorEl}
          transition
          // modifiers={[
          //   {
          //     name: 'preventOverflow',
          //     enabled: true,
          //     options: {
          //       altAxis: true,
          //       altBoundary: true,
          //       tether: false,
          //       rootBoundary: 'viewport',
          //     },
          //   },
          // ]}
          sx={{ zIndex: 10, width: anchorEl && anchorEl?.clientWidth / 2 }}
          placement="bottom-start"
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <div>
                <ClickAwayListener
                  onClickAway={() => {
                    handleClose();
                    setConnectedDefinitions(bkpConnectedDefinitions);
                    if (searchQuery) {
                      setSearchQuery('');
                    }
                  }}
                >
                  <div
                    className={`flex flex-col
                      whitespace-nowrap bg-white rounded-md shadow-lg card-shadow`}
                  >
                    <div className="flex flex-col">
                      {connectedDefinitionsList.isLoading ? (
                        <div className="flex flex-col gap-4 px-4">
                          <Skeleton variant="rounded" height={20} />
                          <Skeleton variant="rounded" height={20} />
                          <Skeleton variant="rounded" height={20} />
                          <Skeleton variant="rounded" height={20} />
                          <Skeleton variant="rounded" height={20} />
                        </div>
                      ) : (
                        <div
                          className="flex flex-col h-fit"
                          style={{ maxHeight: '243px' }}
                        >
                          <div className="p-3 pb-1">
                            <Input
                              leftElem={<MagnifyingGlass size={16} />}
                              rightElem={
                                searchQuery.length > 0 && (
                                  <XCircle
                                    size={16}
                                    onClick={() => {
                                      setSearchQuery('');
                                    }}
                                    className="mr-2 text-pv-neutral-grey-500 cursor-pointer"
                                  />
                                )
                              }
                              className={{
                                input: {
                                  wrapper: 'pr-0 py-2 m-0',
                                },
                              }}
                              value={searchQuery}
                              onChange={(e) => {
                                setSearchQuery(e.target.value);
                              }}
                              iconClassName="left-0 pl-4"
                              placeholder={`Search`}
                            />
                          </div>
                          <div className="flex flex-col overflow-y-auto mx-1.5">
                            {connectedDefinitionsList?.data?.metrics.filter(
                              (dat) =>
                                dat.name
                                  .toLowerCase()
                                  .includes(searchQuery.toLowerCase()) &&
                                (keyDefinitionId === 'new-definition'
                                  ? true
                                  : // : location?.state?.metricInfo._id !== dat._id
                                    metricInfo?._id !== dat._id)
                            ).length > 0 ? (
                              connectedDefinitionsList?.data?.metrics
                                .filter(
                                  (dat) =>
                                    dat.name
                                      .toLowerCase()
                                      .includes(searchQuery.toLowerCase()) &&
                                    (keyDefinitionId === 'new-definition'
                                      ? true
                                      : // : location?.state?.metricInfo._id !== dat._id
                                        metricInfo?._id !== dat._id)
                                )
                                .map((item, index) => {
                                  return (
                                    <div>
                                      <label
                                        htmlFor={item._id}
                                        className={`px-6 flex items-center gap-2 py-3 select-none w-full text-left text-xs truncate ${
                                          !item?.enabled
                                            ? `text-t-disabled cursor-not-allowed`
                                            : `hover:bg-pv-primary-primary-50 text-brand-ai-200 cursor-pointer`
                                        }`}
                                        key={index}
                                      >
                                        <input
                                          id={item._id}
                                          value={item._id}
                                          key={`${item}-${isChecked(item)}`}
                                          type="checkbox"
                                          checked={isChecked(item)}
                                          style={{ accentColor: '#3661ED' }}
                                          disabled={!item?.enabled}
                                          onChange={() => handleChange(item)}
                                        />
                                        <span>{item.name}</span>
                                      </label>
                                    </div>
                                  );
                                })
                            ) : searchQuery ? (
                              <div className="px-4 text-pv-text-secondary-text text-xs">
                                No results for{' '}
                                <b className="text-pv-text-primary-text">
                                  "{searchQuery}"
                                </b>
                              </div>
                            ) : (
                              <div className="text-pv-text-secondary-text">
                                {' '}
                                No data found
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    <div style={{ marginTop: 'auto' }}>
                      <div className="flex justify-between border-t">
                        <div className="flex flex-row justify-end p-2">
                          <TsButton
                            onClick={(e) => {
                              e.stopPropagation();
                              setConnectedDefinitions([]);
                              if (searchQuery) {
                                setSearchQuery('');
                              }
                            }}
                            btnColor="ghost"
                            btnSize="sm"
                            disabled={connectedDefinitions.length < 1}
                          >
                            Reset
                          </TsButton>
                        </div>
                        <div className="flex flex-row justify-end p-2 gap-2">
                          <TsButton
                            btnColor="secondary"
                            btnSize="sm"
                            onClick={() => {
                              setConnectedDefinitions(bkpConnectedDefinitions);
                              if (searchQuery) {
                                setSearchQuery('');
                              }
                              handleClose();
                            }}
                          >
                            Cancel
                          </TsButton>
                          <TsButton
                            onClick={() => {
                              setBkpConnectedDefinitions(connectedDefinitions);
                              handleClose();
                            }}
                            btnSize="sm"
                            disabled={
                              JSON.stringify(connectedDefinitions) ===
                              JSON.stringify(bkpConnectedDefinitions)
                            }
                          >
                            Apply
                          </TsButton>
                        </div>
                      </div>
                    </div>
                  </div>
                </ClickAwayListener>
              </div>
            </Fade>
          )}
        </Popper>
      </div>
    </div>
  );
};

const Sources = ({
  user,
  name,
  sources,
  setSources,
  sourcesTableMapping,
  setSourcesTableMapping,
  sourcesListData,
  tablesListData,
  bkpSources,
  setBkpSources,
  setBkpSourcesTableMapping,
  bkpSourcesTableMapping,
  searchQuery,
  setSearchQuery,
}) => {
  const [listData, setListData] = useState([]);
  const [anchorEl, setAnchorEl] = React.useState();
  const [open, setOpen] = React.useState(false);
  const [activeTable, setActiveTable] = React.useState('');
  const [immediateSearchQuery, setImmediateSearchQuery] = useState('');
  const [node, setRef] = useState();
  const { isBottom } = useDetectScrolledToBottom(node);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [sourcesModalOpen, setSourcesModalOpen] = useState(false);

  useEffect(() => {
    tablesListData?.data && setTotalPages(tablesListData?.data?.totalPages);
    tablesListData?.data &&
      setListData((prev) => {
        let clonedArr = structuredClone(prev);
        return [...clonedArr, ...tablesListData.data.data];
      });
  }, [tablesListData?.data]);

  useEffect(() => {
    if (isBottom === true && currentPageNumber < totalPages) {
      setCurrentPageNumber(currentPageNumber + 1);
    }
  }, [isBottom]);

  const searchDebounce = useCallback(
    debounce((e) => {
      setSearchQuery(e.target.value);
      // setCurrentPageNumber(1);
      // setTotalPages(1);
      // setListData([]);
    }, 300),
    []
  );

  return (
    <div>
      <TsButton
        btnColor="secondary"
        onClick={() => {
          setSourcesModalOpen(true);
        }}
        disabled={user?.role.toLowerCase() !== 'admin'}
      >
        <Plus />
        <span>Add&nbsp;{name}</span>
      </TsButton>
      <SourcesModal
        isOpen={sourcesModalOpen}
        handleCloseModal={() => {
          setSourcesModalOpen(false);
        }}
        name={name}
        integrations={sourcesListData?.data?.data}
        integrationsLoading={sourcesListData?.isLoading}
        sources={sources}
        setSources={setSources}
        sourcesTableMapping={sourcesTableMapping}
        setSourcesTableMapping={setSourcesTableMapping}
        bkpSources={bkpSources}
        bkpSourcesTableMapping={bkpSourcesTableMapping}
        setBkpSources={setBkpSources}
        setBkpSourcesTableMapping={setBkpSourcesTableMapping}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        searchDebounce={searchDebounce}
      />
    </div>
  );
};

export const KeyDefinitionDetail = () => {
  const { user } = useAuth();
  const [form, setForm] = useState({});
  const [bkpForm, setBkpForm] = useState({});
  const [actualSources, setActualSources] = useState({});
  const navigate = useNavigate();
  const [sources, setSources] = useState({});
  const [sourcesTableMapping, setSourcesTableMapping] = useState({});
  const [bkpSources, setBkpSources] = useState({});
  const [bkpSourcesTableMapping, setBkpSourcesTableMapping] = useState({});

  const [stepperIndex, setStepperIndex] = useState(1);
  const [maxIndex, setMaxIndex] = useState(1);
  const [indexAllowed, setIndexAllowed] = useState({
    0: true,
    1: false,
    2: false,
    3: false,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [currKDSelected, setCurrKDSelected] = useState({
    name: '',
    id: '',
  });
  const [sourceSelected, setSourceSelected] = useState({});

  const [colEditModalOpen, setColEditModalOpen] = useState(false);
  const [selectedColDetails, setSelectedColDetails] = useState({});

  const [savingKD, setSavingKD] = useState(true);
  const [canReroute, setCanReroute] = useState(false);

  const [colDataModalOpen, setColDataModalOpen] = useState(false);
  const [columnDetails, setColumnDetails] = useState({});

  const [selTables, setSelTables] = useState([]);
  const [bkpSelTables, setBkpSelTables] = useState([]);
  const [pathDetails, setPathDetails] = useState({});
  const [tablePaths, setTablePaths] = useState({});
  const [bkpTablePaths, setBkpTablePaths] = useState({});
  const [newMetricDetails, setNewMetricDetails] = useState({});
  const [pathsLoading, setPathsLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});

  const createKeyDefinition = useCreateKeyDefinition();
  const updateKeyDefinition = useUpdateKeyDefinition();
  const { keyDefinitionId } = useParams();
  const location = useLocation();
  const logoRef = useRef();
  logoRef.current = location?.state?.log;

  const connectedDefinitionsList = useGetConnectedDefinitionsList({
    config: {
      staleTime: Infinity,
    },
  });

  const sourcesListData = useGetDictionariesList({
    // keyword: searchQuery,
    // columnNameKeyword: searchQuery,
    // pageNumber: currentPageNumber,
    config: {
      staleTime: Infinity,
      // enabled: currentPageNumber === 1 || currentPageNumber <= totalPages,
    },
  });

  const updateKDDrillDowns = updateDrillDownTables();

  const updateDefinition = useUpdateDefinition();

  const updateKDPaths = useUpdateDefinitionMergePaths();

  const {
    setDrillDownPathsRefetch,
    defaultDrillDownPathModalOpen,
    defaultDrillDownPathModalDetail,
    setDefaultDrillDownPathModalOpen,
  } = useExploreStore(
    useShallow((state) => ({
      setDrillDownPathsRefetch: state.setDrillDownPathsRefetch,
      defaultDrillDownPathModalOpen: state.defaultDrillDownPath.modalOpen,
      defaultDrillDownPathModalDetail: state.defaultDrillDownPath.detail,
      setDefaultDrillDownPathModalOpen: state.setDefaultDrillDownPathModalOpen,
    }))
  );

  const logos = useGetLogoList({
    config: {
      staleTime: Infinity,
    },
  });

  let logoList = {};

  if (
    Array.isArray(logos.data?.integrations) &&
    logos.data.integrations.length > 0
  ) {
    logos.data.integrations.forEach((logoInfo) => {
      if (logoInfo.slug && logoInfo.logo) {
        logoList[logoInfo.slug] = logoInfo.logo;
      }
    });
  }

  let metricInfo = {};
  if (
    keyDefinitionId !== 'new-definition' &&
    !connectedDefinitionsList.isLoading &&
    Array.isArray(connectedDefinitionsList.data?.metrics)
  ) {
    metricInfo = connectedDefinitionsList.data.metrics?.filter(
      (dat) => dat._id === keyDefinitionId
    )[0];
  }

  const updateDrillDownTableHandler = async (dDTabs, dDTabsPaths) => {
    const tabData = structuredClone(dDTabs || []);
    const uniqueIds = uniq(tabData.map((item) => item.integId));
    let data = [];
    if (uniqueIds.length > 0) {
      uniqueIds.forEach((id) => {
        const temp = tabData.filter((integ) => integ.integId === id);
        const tempData = {
          name: temp[0].integName,
          dataSourceId: temp[0].integId,
          platform: temp[0].platform,
          tables: temp.map((integ) => ({
            name: integ.tableName,
            dictionaryId: integ.tableId,
          })),
        };
        data.push(tempData);
      });
    }
    try {
      setLoading(true);
      const res = await updateKDDrillDowns.mutateAsync({
        metricId: metricInfo?._id,
        drillDownSources: data,
      });
      setPathDetails(res);
      let paths = {};
      const tables = Object.keys(res?.mergePaths || {});
      if (Array.isArray(tables) && tables.length > 0) {
        tables.forEach((p) => {
          const allPaths = res.mergePaths[p]?.paths;
          if (Array.isArray(allPaths)) {
            if (allPaths.length > 0) {
              if (dDTabsPaths?.[p]) {
                paths[p] = dDTabsPaths[p];
              } else {
                paths[p] = allPaths[0];
              }
            } else if (allPaths.length === 0) {
              if (p === metricInfo?.primarySource) {
                paths[p] = [];
              }
            }
          }
        });
      }
      setTablePaths(paths);
      setBkpTablePaths(paths);
      setBkpSelTables(structuredClone(dDTabs || []));
      setPathsLoading(false);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      return Promise.reject(e);
    }
  };

  useEffect(() => {
    let metricInfo = {};
    if (
      keyDefinitionId !== 'new-definition' &&
      !connectedDefinitionsList.isLoading &&
      Array.isArray(connectedDefinitionsList.data?.metrics)
    ) {
      metricInfo = connectedDefinitionsList.data.metrics?.filter(
        (dat) => dat._id === keyDefinitionId
      )[0];
    }
    if (!connectedDefinitionsList.isLoading) {
      if (keyDefinitionId !== 'new-definition') {
        setForm({
          name: metricInfo?.name,
          description: metricInfo?.description ?? '',
          code_gen_desc: metricInfo?.code_gen_desc ?? '',
          category: metricInfo?.category,
          enabled: metricInfo?.enabled ?? true,
          enabledForSearch: metricInfo?.enabledForSearch ?? true,
          drillDownSources: metricInfo?.drillDownSources,
          connectedMetricIds: metricInfo?.connectedMetricIds,
          sources: metricInfo?.sources,
        });
        setBkpForm({
          name: metricInfo?.name,
          description: metricInfo?.description ?? '',
          code_gen_desc: metricInfo?.code_gen_desc ?? '',
          category: metricInfo?.category,
          enabled: metricInfo?.enabled ?? true,
          enabledForSearch: metricInfo?.enabledForSearch ?? true,
          drillDownSources: metricInfo?.drillDownSources,
          connectedMetricIds: metricInfo?.connectedMetricIds,
          sources: metricInfo?.sources,
        });

        let manipulateSources = {};
        let manipulatedSourcesTableMapping = {};
        Array.isArray(metricInfo?.sources) &&
          metricInfo.sources.length > 0 &&
          metricInfo.sources.map((mI) => {
            mI.tables.map(async (mIT) => {
              manipulateSources[mIT.dictionaryId] = mIT.columns;
              manipulatedSourcesTableMapping[mIT.dictionaryId] = {};
              manipulatedSourcesTableMapping[mIT.dictionaryId][
                'datasource_description'
              ] = mI.name;
              manipulatedSourcesTableMapping[mIT.dictionaryId][
                'integrationId'
              ] = mI.dataSourceId;
              manipulatedSourcesTableMapping[mIT.dictionaryId]['tableName'] =
                mIT.name;
              manipulatedSourcesTableMapping[mIT.dictionaryId]['platform'] =
                mI.platform;
              let logo = logoRef.current?.integrations?.find(
                (log) => log.slug === mI.platform
              );
              manipulatedSourcesTableMapping[mIT.dictionaryId][
                'integration_logo'
              ] = logo?.logo;
            });
          });
        setSources(manipulateSources);
        setSourcesTableMapping(manipulatedSourcesTableMapping);
        setBkpSources(manipulateSources);
        setBkpSourcesTableMapping(manipulatedSourcesTableMapping);
        setActualSources(manipulateSources);

        if (
          metricInfo?.primarySource?.length &&
          Array.isArray(metricInfo?.sources) &&
          metricInfo.sources.length > 0
        ) {
          metricInfo.sources.forEach((src) => {
            if (Array.isArray(src?.tables) && src.tables.length) {
              const ind = src.tables.findIndex(
                (tab) => tab?.name === metricInfo.primarySource
              );
              if (ind > -1) {
                setNewMetricDetails({
                  primarySource: metricInfo.primarySource,
                  platform: src.platform,
                  metricId: metricInfo._id,
                  metricName: metricInfo.name,
                });
              }
            }
          });
        }

        let dDTabs = [];
        let dDTabsPaths = {};
        if (
          Array.isArray(metricInfo?.drillDownSources) &&
          metricInfo.drillDownSources.length > 0
        ) {
          metricInfo.drillDownSources.forEach((dD) => {
            if (Array.isArray(dD?.tables) && dD.tables.length > 0) {
              dD.tables.forEach((tab) => {
                if (Array.isArray(tab?.definedPath?.path)) {
                  dDTabsPaths[tab?.name] = tab.definedPath.path;
                }
              });
              const temp = dD.tables.map((tab) => ({
                integName: dD.name,
                integId: dD.dataSourceId,
                platform: dD.platform,
                tableName: tab.name,
                tableId: tab.dictionaryId,
              }));
              dDTabs.push(...temp);
            }
          });
        }

        if (dDTabs.length > 0) {
          updateDrillDownTableHandler(dDTabs, dDTabsPaths);
          // setDrillDownPathsRefetch(true);
        } else {
          setPathsLoading(false);
        }

        setSelTables(dDTabs);
        setBkpSelTables(dDTabs);

        setIndexAllowed({
          0: true,
          1: true,
          2: true,
          3: true,
        });
        setMaxIndex(4);
      } else if (
        keyDefinitionId === 'new-definition' &&
        location.state?.type === 'clone'
      ) {
        metricInfo = location.state?.metricInfo;
        setForm({
          name: metricInfo?.name,
          description: metricInfo?.description,
          code_gen_desc: metricInfo?.code_gen_desc ?? '',
          category: metricInfo?.category,
          enabled: metricInfo?.enabled ?? true,
          enabledForSearch: metricInfo?.enabledForSearch ?? true,
          drillDownSources: metricInfo?.drillDownSources,
          connectedMetricIds: metricInfo?.connectedMetricIds,
          sources: metricInfo?.sources,
        });

        let manipulateSources = {};
        let manipulatedSourcesTableMapping = {};
        Array.isArray(metricInfo?.sources) &&
          metricInfo.sources.length > 0 &&
          metricInfo.sources.map((mI) => {
            mI.tables.map(async (mIT) => {
              manipulateSources[mIT.dictionaryId] = mIT.columns;
              manipulatedSourcesTableMapping[mIT.dictionaryId] = {};
              manipulatedSourcesTableMapping[mIT.dictionaryId][
                'datasource_description'
              ] = mI.name;
              manipulatedSourcesTableMapping[mIT.dictionaryId][
                'integrationId'
              ] = mI.dataSourceId;
              manipulatedSourcesTableMapping[mIT.dictionaryId]['tableName'] =
                mIT.name;
              manipulatedSourcesTableMapping[mIT.dictionaryId]['platform'] =
                mI.platform;
              let logo = logoRef.current?.integrations?.find(
                (log) => log.slug === mI.platform
              );
              manipulatedSourcesTableMapping[mIT.dictionaryId][
                'integration_logo'
              ] = logo?.logo;
            });
          });
        setSources(manipulateSources);
        setSourcesTableMapping(manipulatedSourcesTableMapping);
        setBkpSources(manipulateSources);
        setBkpSourcesTableMapping(manipulatedSourcesTableMapping);
        // setActualSources(manipulateSources);

        setIndexAllowed({
          0: true,
          1: true,
          2: true,
          3: true,
        });
        setMaxIndex(4);
      } else {
        setForm({
          name: '',
          description: '',
          code_gen_desc: '',
          category: '',
          enabled: false,
          enabledForSearch: false,
        });
        setBkpForm({
          name: '',
          description: '',
          code_gen_desc: '',
          category: '',
          enabled: false,
          enabledForSearch: false,
        });
      }
    }
  }, [connectedDefinitionsList.data, currKDSelected?.id, location.state]);

  useEffect(() => {
    setStepperIndex(1);
  }, [currKDSelected?.id, location.state]);

  useEffect(() => {
    if (!savingKD) setSavingKD(true);
    if (canReroute) setCanReroute(false);
  }, [keyDefinitionId]);

  useEffect(() => {
    sourcesListData.refetch();
  }, [currKDSelected?.id, stepperIndex]);

  let sourceList = [];
  Object.keys(bkpSources) &&
    Object.keys(bkpSources).forEach((source) => {
      bkpSources[source].forEach((src) => {
        sourceList.push({ ...src, sourceId: source });
      });
    });

  const handleDeleteColumn = ({ name, sourceId }) => {
    let temp = structuredClone(bkpSources || {});
    let tempMapping = structuredClone(bkpSourcesTableMapping || {});
    temp[sourceId] = temp[sourceId].filter((col) => col.name !== name);
    if (temp[sourceId].length === 0) {
      delete temp[sourceId];
      delete tempMapping[sourceId];
    }
    setSources(temp);
    setBkpSources(temp);
    setSourcesTableMapping(tempMapping);
    setBkpSourcesTableMapping(tempMapping);
  };

  const re = /^ +$/;

  useEffect(() => {
    const { name, description, code_gen_desc, category } = form;
    if (
      keyDefinitionId === 'new-definition' &&
      location.state?.type !== 'clone' &&
      maxIndex === 1
    ) {
      if (
        name?.length > 0 &&
        !re.test(name) &&
        description?.length > 0 &&
        !re.test(description) &&
        code_gen_desc?.length > 0 &&
        !re.test(code_gen_desc) &&
        category !== ''
      ) {
        setIndexAllowed({
          0: true,
          1: true,
          2: false,
          3: false,
        });
      } else {
        setIndexAllowed({
          0: true,
          1: false,
          2: false,
          3: false,
        });
      }
    }
  }, [form, keyDefinitionId, maxIndex]);

  const stepperCond = () => {
    const bool =
      form.name?.length === 0 ||
      re.test(form.name) ||
      form.description?.length === 0 ||
      re.test(form.description) ||
      form.code_gen_desc?.length === 0 ||
      re.test(form.code_gen_desc) ||
      form.category === '';
    return bool;
  };

  const diffPaths = () => {
    const temp = structuredClone(tablePaths || {});
    const bkpTemp = structuredClone(bkpTablePaths || {});
    const tempTabs = Object.keys(temp);
    const bkpTempTabs = Object.keys(bkpTemp);
    if (Array.isArray(tempTabs) && Array.isArray(bkpTempTabs)) {
      let tempPaths = {};
      let bkpTempPaths = {};
      tempTabs.forEach((tab) => {
        if (Array.isArray(temp[tab])) {
          tempPaths[tab] = temp[tab].map((p) => ({
            from: p.from,
            how: p.how,
            left_on: p.left_on,
            right_on: p.right_on,
            to: p.to,
          }));
        }
      });
      bkpTempTabs.forEach((tab) => {
        if (Array.isArray(bkpTemp[tab])) {
          bkpTempPaths[tab] = bkpTemp[tab].map((p) => ({
            from: p.from,
            how: p.how,
            left_on: p.left_on,
            right_on: p.right_on,
            to: p.to,
          }));
        }
      });
      return !isEqual(tempPaths, bkpTempPaths);
    } else {
      return false;
    }
  };

  const updateDrillDownHandler = async () => {
    const tabs = structuredClone(tablePaths || {});
    const tableIds = Object.keys(structuredClone(tabs) || {});
    const tabData = structuredClone(selTables || []);
    const uniqueIds = uniq(tabData.map((item) => item.integId));
    if (newMetricDetails?.metricId?.length > 0 && Array.isArray(tableIds)) {
      let data = [];
      if (uniqueIds.length > 0) {
        uniqueIds.forEach((id) => {
          const temp = tabData.filter((integ) => integ.integId === id);
          const tempData = {
            name: temp[0].integName,
            dataSourceId: temp[0].integId,
            platform: temp[0].platform,
            tables: temp.map((integ) => ({
              name: integ.tableName,
              dictionaryId: integ.tableId,
            })),
          };
          data.push(tempData);
        });
      }
      const paths = structuredClone(pathDetails || {});
      const tempPathDetails = Object.keys(paths?.mergePaths);
      let drilldowns = {};
      let globalPathdrillDowns = [];
      if (
        tableIds.length > 0 &&
        Array.isArray(tempPathDetails) &&
        tempPathDetails.length > 0
      ) {
        tableIds.forEach((id) => {
          if (id === newMetricDetails?.primarySource) {
            drilldowns[id] = [];
          } else {
            drilldowns[id] = tabs[id].map((p) => ({
              from: p.from,
              how: p.how,
              left_on: p.left_on,
              right_on: p.right_on,
              to: p.to,
            }));
            if (
              paths.mergePaths[id].globalPath &&
              isEqual(tabs[id], paths.mergePaths[id].paths[0])
            ) {
              globalPathdrillDowns.push(id);
            }
          }
        });
      }
      try {
        await updateKDPaths.mutateAsync({
          payload: {
            metricId: newMetricDetails.metricId,
            drillDownSources: data,
            drillDownsPath: drilldowns,
            globalPathDrilldowns: globalPathdrillDowns,
          },
          metadata: {
            update: true,
          },
        });
        setBkpTablePaths(tabs);
      } catch {}
    }
  };

  const handleSave = async () => {
    setMaxIndex(4);
    const temp = {
      metricId: metricInfo?._id,
      description: form?.description,
      category: form?.category,
      updating: true,
      name: form?.name,
      enabled: form?.enabled,
    };
    const bkpTemp = {
      metricId: metricInfo?._id,
      description: bkpForm?.description,
      category: bkpForm?.category,
      updating: true,
      name: bkpForm?.name,
      enabled: bkpForm?.enabled,
    };
    let updaters = [];
    if (!isEqual(temp, bkpTemp)) {
      const data = {
        metricId: metricInfo?._id,
        ...(temp.description !== bkpTemp.description && {
          description: form?.description,
        }),
        ...(temp.category !== bkpTemp.category && {
          category: form?.category,
        }),
        ...(temp.name !== bkpTemp.name && {
          name: form?.name,
        }),
        ...(temp.enabled !== bkpTemp.enabled && {
          enabled: form?.enabled,
        }),
        updating: true,
      };
      updaters.push(async () => {
        await updateDefinition.mutateAsync({
          payload: { ...data },
          metadata: {
            name: temp.name,
          },
        });
      });
    }
    if (diffPaths()) {
      updaters.push(async () => {
        await updateDrillDownHandler();
      });
    }
    try {
      setUpdating(true);
      await Promise.all(updaters.map((updater) => updater()));
      setUpdating(false);
      setSavingKD(false);
      setCanReroute(true);
    } catch {
      setUpdating(false);
    }
  };

  const colNameExtractor = (source) => {
    if (Array.isArray(Object.keys(source)) && Object.keys(source).length > 0) {
      let list = {};
      const tempArr = structuredClone(source);
      Object.keys(tempArr).forEach((tab) => {
        if (Array.isArray(tempArr[tab]) && tempArr[tab].length > 0) {
          const temp = tempArr[tab].map((col) => ({
            name: col.name,
          }));
          list[tab] = temp;
        }
      });
      return list;
    } else {
      return {};
    }
  };

  let bkpSourceList = colNameExtractor(bkpSources);
  let actualSourceList = colNameExtractor(actualSources);

  const blockCondition = () => {
    return (
      (!isEqual(form, bkpForm) ||
        !isEqual(bkpSourceList, actualSourceList) ||
        diffPaths() ||
        Object?.keys(tablePaths)?.length !== selTables?.length) &&
      savingKD
    );
  };

  const { showModal, handleProceed, handleStay } =
    useBlockNavigation(blockCondition);

  useEffect(() => {
    if (!savingKD && canReroute) {
      navigate('/data-catalog');
    }
  }, [savingKD, canReroute]);

  return (
    <LayoutCol span={23} className="h-screen flex flex-col bg-brand-chat-400">
      <div
        className="bg-white border-b border-b-pv-neutral-grey-100 px-6 py-4 h-[71px] flex items-center"
        style={{
          zIndex: 0,
        }}
      >
        <h1 className="flex items-center text-base font-medium leading-7 text-brand-ai-200">
          Data Hub
        </h1>
      </div>
      <div className="flex h-11 bg-white border-b border-pv-neutral-grey-100 items-center">
        <span
          className="ml-4 text-pv-text-secondary-text hover:text-pv-text-primary-text cursor-pointer"
          onClick={() => {
            navigate(`/data-catalog`);
          }}
        >
          Definitions
        </span>
        <CaretRight className="ml-3 text-pv-text-secondary-text" size={16} />
        <KDDropdown
          kDData={connectedDefinitionsList?.data?.metrics}
          setCurrKDSelected={setCurrKDSelected}
          currId={location.state?.metricInfo || metricInfo || {}}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          setStepperIndex={setStepperIndex}
          loading={
            connectedDefinitionsList.isLoading || pathsLoading || loading
          }
        />
      </div>
      <div
        className="flex"
        style={{
          height: `calc(100vh - 115px)`,
          zIndex: 0,
        }}
      >
        <div className="flex w-full h-full overflow-x-auto scrollbar-hide p-0">
          <div
            style={{
              minWidth: '1050px',
              zIndex: 0,
            }}
            className="flex flex-col bg-white w-full"
          >
            <div
              className="flex flex-col gap-4 flex-1 overflow-y-auto"
              style={{
                zIndex: 1,
                height: 'calc(100% - 63px)',
              }}
            >
              {stepperIndex === 1 && (
                <>
                  <div className="font-medium text-pv-text-primary-text text-base border-b border-pv-neutral-grey-100 p-4">
                    <span className="font-semibold">View Definition - </span>
                    <span className="font-medium">Sources</span>
                  </div>
                  <div className="flex flex-col w-full gap-1 pb-3 text-pv-text-secondary-text border-b border-pv-neutral-grey-100 px-4">
                    <span className="font-medium text-pv-text-primary-text">
                      Formula
                    </span>
                    <Markdown>{form.code_gen_desc}</Markdown>
                  </div>
                  <div
                    className="flex flex-col gap-6 w-full px-4 pb-4"
                    style={{
                      zIndex: 0,
                    }}
                  >
                    <div
                      className="w-full gap-2 flex items-center justify-between"
                      style={{
                        zIndex: 1,
                      }}
                    >
                      <div className="flex flex-col text-sm gap-1">
                        <span className="text-pv-text-primary-text flex gap-2 items-center">
                          <span className="font-medium text-pv-text-primary-text">
                            Sources
                          </span>
                          {sourceList.length > 0 && (
                            <span className="text-white text-1xs bg-pv-primary-primary-500 h-fit py-1 px-1.5 rounded-md">
                              {sourceList.length}
                            </span>
                          )}
                        </span>
                        <p className="text-pv-text-secondary-text">
                          Sources enable the system to accurately retrieve the
                          right data for your definitions when used in an
                          analysis
                        </p>
                      </div>

                      {/* <Sources
                        user={user}
                        name="Sources"
                        sources={sources}
                        setSources={setSources}
                        sourcesTableMapping={sourcesTableMapping}
                        setSourcesTableMapping={setSourcesTableMapping}
                        sourcesListData={sourcesListData}
                        bkpSources={bkpSources}
                        setBkpSources={setBkpSources}
                        bkpSourcesTableMapping={bkpSourcesTableMapping}
                        setBkpSourcesTableMapping={setBkpSourcesTableMapping}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                      /> */}
                    </div>

                    <div
                      className="flex flex-col w-full mt-1"
                      style={{
                        zIndex: 0,
                      }}
                    >
                      <div className="flex w-full items-center gap-3">
                        <span className="text-pv-text-secondary-text text-xs">
                          User&nbsp;picked&nbsp;columns
                        </span>
                        <span
                          className="w-full bg-pv-primary-primary-100"
                          style={{
                            height: '1px',
                          }}
                        ></span>
                      </div>
                      {isEqual(sources, {}) || sourceList.length === 0 ? (
                        connectedDefinitionsList.isLoading ? (
                          <div className="flex flex-col w-full">
                            <div
                              className="grid my-4 px-2"
                              style={{
                                gridTemplateColumns:
                                  // '3% 10% 10% 10% 10% 44% 10% 3%',
                                  '3% 10% 10% 10% 10% 47% 10%',
                              }}
                            >
                              <Skeleton
                                variant="rounded"
                                width="19px"
                                height="19px"
                                className="bg-pv-neutral-grey-100 mb-2"
                              />
                              <Skeleton
                                variant="rounded"
                                width="70px"
                                height="19px"
                                className="bg-pv-neutral-grey-100 mb-2"
                              />
                              <Skeleton
                                variant="rounded"
                                width="70px"
                                height="19px"
                                className="bg-pv-neutral-grey-100 mb-2"
                              />
                              <Skeleton
                                variant="rounded"
                                width="70px"
                                height="19px"
                                className="bg-pv-neutral-grey-100 mb-2"
                              />
                              <Skeleton
                                variant="rounded"
                                width="70px"
                                height="19px"
                                className="bg-pv-neutral-grey-100 mb-2"
                              />
                              <Skeleton
                                variant="rounded"
                                width="120px"
                                height="19px"
                                className="bg-pv-neutral-grey-100 mb-2"
                              />
                              <Skeleton
                                variant="rounded"
                                width="70px"
                                height="19px"
                                className="bg-pv-neutral-grey-100 mb-2"
                              />
                            </div>
                            <div className="flex flex-col w-full gap-2">
                              {Array.from({ length: 5 }).map((_, ind) => {
                                return (
                                  <div
                                    className="grid px-2 h-[58px] items-center border border-pv-neutral-grey-100 rounded-lg"
                                    style={{
                                      gridTemplateColumns:
                                        // '3% 10% 10% 10% 10% 44% 10% 3%',
                                        '3% 10% 10% 10% 10% 47% 10%',
                                    }}
                                  >
                                    <Skeleton
                                      variant="rounded"
                                      width="19px"
                                      height="19px"
                                      className="bg-pv-neutral-grey-100 mb-2"
                                    />
                                    <Skeleton
                                      variant="rounded"
                                      width={ind % 2 === 0 ? '80px' : '60px'}
                                      height="19px"
                                      className="bg-pv-neutral-grey-100 mb-2"
                                    />
                                    <Skeleton
                                      variant="rounded"
                                      width={ind % 2 === 1 ? '80px' : '60px'}
                                      height="19px"
                                      className="bg-pv-neutral-grey-100 mb-2"
                                    />
                                    <Skeleton
                                      variant="rounded"
                                      width={ind % 2 === 0 ? '80px' : '60px'}
                                      height="19px"
                                      className="bg-pv-neutral-grey-100 mb-2"
                                    />
                                    <Skeleton
                                      variant="rounded"
                                      width={ind % 2 === 1 ? '80px' : '60px'}
                                      height="19px"
                                      className="bg-pv-neutral-grey-100 mb-2"
                                    />
                                    <Skeleton
                                      variant="rounded"
                                      width={ind % 2 === 1 ? '300px' : '400px'}
                                      height="19px"
                                      className="bg-pv-neutral-grey-100 mb-2"
                                    />
                                    <Skeleton
                                      variant="rounded"
                                      width="70px"
                                      height="19px"
                                      className="bg-pv-neutral-grey-100 mb-2"
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ) : (
                          <div className="w-full flex justify-center my-5">
                            <span className="text-pv-text-secondary-text text-xs">
                              No sources added
                            </span>
                          </div>
                        )
                      ) : (
                        <div className="flex flex-col">
                          <div
                            className="grid my-4 px-2"
                            style={{
                              gridTemplateColumns:
                                // '3% 10% 10% 10% 10% 44% 10% 3%',
                                '3% 10% 10% 10% 10% 47% 10%',
                            }}
                          >
                            <span className="text-xs text-pv-text-secondary-text font-medium px-2">
                              #
                            </span>
                            <span className="text-xs text-pv-text-secondary-text font-medium px-2">
                              Field Name
                            </span>
                            <span className="text-xs text-pv-text-secondary-text font-medium px-2">
                              Table
                            </span>
                            <span className="text-xs text-pv-text-secondary-text font-medium px-2">
                              Column
                            </span>
                            <span className="text-xs text-pv-text-secondary-text font-medium px-2">
                              Data Type
                            </span>
                            <span className="text-xs text-pv-text-secondary-text font-medium px-2">
                              Description
                            </span>
                            <span className="text-xs text-pv-text-secondary-text font-medium px-2">
                              Sample&nbsp;Data
                            </span>
                          </div>
                          <div className="flex flex-col w-full gap-2">
                            {sourceList.map((source, index) => {
                              return (
                                <SourcesElement
                                  user={user}
                                  source={source}
                                  ind={index}
                                  selected={sourceSelected}
                                  setSelected={setSourceSelected}
                                  handleDeleteColumn={handleDeleteColumn}
                                  setColEditModalOpen={setColEditModalOpen}
                                  setSelectedColDetails={setSelectedColDetails}
                                  setColDataModalOpen={setColDataModalOpen}
                                  setColumnDetails={setColumnDetails}
                                  logos={logos}
                                  metricInfo={metricInfo}
                                />
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {stepperIndex === 2 && (
                <>
                  <div className="flex flex-col w-full h-full">
                    <div className="font-medium text-pv-text-primary-text text-base border-b border-pv-neutral-grey-100 p-4">
                      <span className="font-semibold">View Definition - </span>
                      <span className="font-medium">Drill-downs</span>
                    </div>
                    <div
                      className="flex w-full"
                      style={{
                        height: 'calc(100% - 57px)',
                      }}
                    >
                      <SelectDrillDowns
                        searchMetricDetail={0}
                        newMetricDetails={newMetricDetails}
                        reportMsgFetch={() => {}}
                        logoList={logoList}
                        selTables={selTables}
                        setSelTables={setSelTables}
                        bkpSelTables={bkpSelTables}
                        setBkpSelTables={setBkpSelTables}
                        pathDetails={pathDetails}
                        setPathDetails={setPathDetails}
                        tablePaths={tablePaths}
                        setTablePaths={setTablePaths}
                        bkpTablePaths={0}
                        setBkpTablePaths={() => {}}
                        loading={loading}
                        setLoading={setLoading}
                        selected={selected}
                        setSelected={setSelected}
                        pathsLoading={pathsLoading}
                      />
                    </div>
                  </div>
                </>
              )}

              {stepperIndex === 3 && (
                <>
                  <div className="font-medium text-pv-text-primary-text text-base border-b border-pv-neutral-grey-100 p-4">
                    <span className="font-semibold">View Definition - </span>
                    <span className="font-medium">Details</span>
                  </div>
                  <div className="text-sm flex flex-col gap-2 text-brand-ai-200 px-4">
                    <div>Name*</div>
                    <Input
                      className={{
                        input: {
                          wrapper: `pr-0 py-2 m-0 ${
                            updating && 'cursor-not-allowed'
                          }`,
                        },
                      }}
                      value={form.name}
                      onChange={(e) =>
                        setForm({ ...form, name: e?.target.value })
                      }
                      placeholder="Enter the name of definition"
                      disabled={updating}
                    />
                  </div>

                  <div className="text-sm flex flex-col gap-2 text-brand-ai-200 px-4">
                    <div>Description*</div>
                    <Input
                      type="textarea"
                      className={{
                        input: {
                          wrapper: `pr-0 py-2 m-0 ${
                            updating && 'cursor-not-allowed'
                          }`,
                        },
                      }}
                      value={form.description}
                      labelTextClassName="text-sm flex flex-col gap-2 text-brand-ai-200"
                      placeholder="Enter the description"
                      onChange={(e) =>
                        setForm({ ...form, description: e?.target.value })
                      }
                      minRows={3}
                      maxRows={3}
                      disabled={updating}
                    />
                  </div>

                  <div className="text-sm flex flex-col gap-2 text-brand-ai-200 px-4">
                    <div>Formula*</div>
                    <Input
                      type="textarea"
                      className={{
                        input: {
                          wrapper: `pr-0 py-2 m-0 border-transparent cursor-not-allowed`,
                          root: 'text-pv-text-disabled-text',
                        },
                      }}
                      value={form.code_gen_desc}
                      labelTextClassName="text-sm flex flex-col gap-2 text-brand-ai-200"
                      placeholder="Enter the formula"
                      onChange={(e) =>
                        setForm({ ...form, code_gen_desc: e?.target.value })
                      }
                      minRows={3}
                      maxRows={3}
                      disabled
                    />
                  </div>

                  <div className="w-full gap-2 flex flex-col px-4">
                    <div className="text-sm text-brand-ai-200">Type*</div>

                    <Type
                      user={user}
                      form={form}
                      setForm={setForm}
                      disabled={updating}
                    />
                  </div>

                  <div className="w-full gap-2 flex flex-col px-4">
                    <div className="w-full gap-2 flex flex-col">
                      <div className="text-sm flex text-brand-ai-200">
                        Status
                      </div>
                      <div
                        className={twMerge(
                          `group items-center justify-between flex border border-brand-ai-300 rounded-lg hover:border-brand-ai-100 focus:ring-brand-ai-100 focus:border-brand-ai-100 py-2 px-3 cursor-pointer`,
                          updating &&
                            'cursor-not-allowed border-transparent bg-pv-neutral-grey-50 hover:border-transparent text-pv-text-disabled-text'
                        )}
                        onClick={(e) => {
                          if (!updating) {
                            setForm({ ...form, enabled: !form?.enabled });
                          }
                        }}
                      >
                        <div
                          className="items-center flex ml-1"
                          style={{ color: !form.enabled && '#ABADB1' }}
                        >
                          Enable for Search
                        </div>
                        <LatestToggle
                          size="sm"
                          disabled={updating}
                          checked={form.enabled}
                          onChange={(e) => {
                            setForm({ ...form, enabled: e?.value });
                          }}
                          className={{
                            toggle: {
                              root: `${
                                !updating
                                  ? form?.enabled
                                    ? 'group-hover:bg-pv-primary-primary-800'
                                    : 'group-hover:bg-pv-neutral-grey-500'
                                  : ''
                              }`,
                            },
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              {stepperIndex === 4 && (
                <>
                  <div className="w-7/12 gap-2 flex flex-col">
                    <div className="text-sm text-brand-ai-200">
                      Add as filter
                    </div>
                    <div
                      className={twJoin(
                        `group items-center justify-between flex border border-brand-ai-300 rounded-lg hover:border-brand-ai-100 focus:ring-brand-ai-100 focus:border-brand-ai-100 py-2 px-3 cursor-pointer`,
                        (user?.role.toLowerCase() !== 'admin' ||
                          !form.enabled) &&
                          'cursor-not-allowed'
                      )}
                      onClick={(e) => {
                        if (
                          user?.role.toLowerCase() === 'admin' &&
                          form.enabled
                        ) {
                          setForm({
                            ...form,
                            enabledForSearch: !form?.enabledForSearch,
                          });
                        }
                      }}
                    >
                      <div
                        className="items-center flex ml-1"
                        style={{
                          color:
                            (!form.enabledForSearch || !form.enabled) &&
                            '#abadb1',
                        }}
                      >
                        Utilize in Search Filter on Explore Tab
                      </div>
                      <LatestToggle
                        size="sm"
                        disabled={
                          user?.role.toLowerCase() !== 'admin' || !form.enabled
                        }
                        checked={form.enabledForSearch}
                        onChange={(e) => {
                          setForm({
                            ...form,
                            enabledForSearch: e?.value,
                          });
                        }}
                        className={{
                          toggle: {
                            root: `${
                              form?.enabled &&
                              user?.role.toLowerCase() === 'admin'
                                ? form?.enabledForSearch
                                  ? 'group-hover:bg-pv-primary-primary-800'
                                  : 'group-hover:bg-pv-neutral-grey-500'
                                : ''
                            }`,
                          },
                        }}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
            <div
              className="relative h-[63px] w-full"
              style={{
                zIndex: 0,
              }}
            >
              <div
                className="flex justify-between p-4 border-t items-end gap-1.5"
                style={{ marginTop: 'auto' }}
              >
                <TsButton
                  onClick={() => {
                    setSavingKD(false);
                    setCanReroute(true);
                  }}
                  btnColor="ghost"
                  disabled={updating}
                >
                  Cancel
                </TsButton>
                <div className="flex gap-3">
                  {stepperIndex > 1 && (
                    <TsButton
                      btnColor="secondary"
                      disabled={stepperIndex === 1 || updating}
                      onClick={() => {
                        setStepperIndex((prev) => prev - 1);
                      }}
                    >
                      Previous Step
                    </TsButton>
                  )}
                  {stepperIndex === 3 ? (
                    <TsButton
                      disabled={
                        form.name === '' ||
                        form.description === '' ||
                        form.category === '' ||
                        (isEqual(actualSourceList, bkpSourceList) &&
                          JSON.stringify(form) === JSON.stringify(bkpForm) &&
                          !diffPaths()) ||
                        user?.role.toLowerCase() !== 'admin' ||
                        pathsLoading ||
                        Object?.keys(tablePaths)?.length !==
                          selTables?.length ||
                        updating
                      }
                      onClick={async () => {
                        await handleSave();
                      }}
                    >
                      {updating ? (
                        <span className="flex items-center gap-1.5">
                          <img src={spinner} width="14px" height="14px" />
                          <span>Updating</span>
                        </span>
                      ) : (
                        'Update'
                      )}
                    </TsButton>
                  ) : (
                    <TsButton
                      onClick={() => {
                        setStepperIndex((prev) => {
                          if (maxIndex < prev + 1) {
                            setMaxIndex(prev + 1);
                          }
                          if (indexAllowed[prev + 1] === false) {
                            setIndexAllowed((prevArr) => ({
                              ...prevArr,
                              [prev + 1]: true,
                            }));
                          }
                          return prev + 1;
                        });
                      }}
                      disabled={
                        connectedDefinitionsList.isLoading ||
                        (keyDefinitionId === 'new-definition' &&
                          !indexAllowed[stepperIndex]) ||
                        (keyDefinitionId === 'new-definition' &&
                          location.state?.type === 'clone' &&
                          (form.name?.length === 0 ||
                            re.test(form.name) ||
                            form.description?.length === 0 ||
                            re.test(form.description) ||
                            form.code_gen_desc?.length === 0 ||
                            re.test(form.code_gen_desc) ||
                            form.category === '')) ||
                        (keyDefinitionId !== 'new-definition' &&
                          (form.name?.length === 0 ||
                            re.test(form.name) ||
                            form.description?.length === 0 ||
                            re.test(form.description) ||
                            form.code_gen_desc?.length === 0 ||
                            re.test(form.code_gen_desc) ||
                            form.category === ''))
                      }
                    >
                      Next
                    </TsButton>
                  )}
                </div>
              </div>
              <div className="bg-white flex absolute left-1/2 transform -translate-x-1/2 top-1/2 -translate-y-1/2 gap-2">
                {[
                  'Sources',
                  'Drill-downs',
                  'Details',
                  // 'Validate Definition',
                ].map((step, ind) => {
                  return (
                    <div className="flex items-center gap-2" key={ind}>
                      {ind > 0 && (
                        <span
                          className={`w-10 h-[1px] ${
                            ind > 0 && stepperCond()
                              ? 'bg-pv-neutral-grey-400'
                              : maxIndex >= ind + 1
                                ? 'bg-pv-primary-primary-500'
                                : 'bg-pv-neutral-grey-400'
                          }`}
                        ></span>
                      )}
                      <button
                        className={twMerge(
                          'cursor-pointer flex items-center gap-1 rounded-lg px-2 py-1',
                          ind > 0 && stepperCond()
                            ? 'text-pv-neutral-grey-400'
                            : maxIndex >= ind + 1 ||
                                keyDefinitionId !== 'new-definition'
                              ? `text-pv-primary-primary-500 hover:text-pv-primary-primary-800`
                              : 'text-pv-neutral-grey-400',
                          stepperIndex === ind + 1
                            ? 'bg-pv-primary-primary-50'
                            : ''
                        )}
                        disabled={
                          (ind > 0 && !indexAllowed[ind + 1]) ||
                          (ind > 0 && stepperCond()) ||
                          updating
                        }
                        onClick={() => {
                          setStepperIndex(ind + 1);
                        }}
                      >
                        {maxIndex > ind + 1 ? (
                          <CheckCircle weight="fill" size={16} />
                        ) : (
                          <div className="w-4 h-4 flex justify-center items-center">
                            <Circle weight="bold" size={16} />
                          </div>
                        )}
                        <span>{step}</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        style={{
          zIndex: 1,
        }}
      >
        <EditColumnDetailModal
          isOpen={colEditModalOpen}
          handleCloseModal={() => {
            setColEditModalOpen(false);
          }}
          selectedColDetails={selectedColDetails}
          setBkpSources={setBkpSources}
          setSources={setSources}
        />
      </div>
      <div
        style={{
          zIndex: 1,
        }}
      >
        <RouterKeyDefPauseModal
          isOpen={showModal}
          handleProceed={handleProceed}
          handleStay={handleStay}
          setSavingKD={setSavingKD}
          // handleLeave={async () => {
          //   await handleSave();
          // }}
        />
      </div>

      <div
        style={{
          zIndex: 1,
        }}
      >
        <ColumnSampleDataModal
          isOpen={colDataModalOpen}
          handleClose={() => {
            setColDataModalOpen(false);
          }}
          columnDetails={columnDetails}
        />
      </div>
      <div
        style={{
          zIndex: 1,
        }}
      >
        <ChangeDefaultPathModal
          isOpen={defaultDrillDownPathModalOpen}
          handleCloseModal={() => {
            setDefaultDrillDownPathModalOpen(false);
          }}
          defPathModalDetail={defaultDrillDownPathModalDetail}
        />
      </div>
    </LayoutCol>
  );
};
